import CakeLoader from "@/Components/loaders/cakeLoader";
import ModalConflictoCarrito from "@/Components/modal/ModalConflictoCarrito";
import Product from "@/Components/product/product";
import LayoutPrincipal from "@/Layout/principal";
import { useCartStore } from "@/stores/useCart";
import {
    CalendarType,
    DiaBloqueado,
    DisponibilidadDespacho,
    ICantidadPersonas,
    IImagenesProducto,
    IProducto,
    IProductoOpcion,
    ISabor,
    ISeccion,
    ISubseccion,
    ITiendaProductos,
} from "@/types/app";
import { Sexo } from "@/types/enums";
import {
    PattyToast,
    capturarError,
    delay,
    formater,
    separadorMiles,
} from "@/utils/global";
import { router } from "@inertiajs/react";
import * as Icon from "@phosphor-icons/react/dist/ssr";
import axios from "axios";
import React, { ReactNode, Suspense, useCallback, useEffect, useMemo, useState } from "react";
const Calendar = React.lazy(() => import("react-calendar"));
import "react-calendar/dist/Calendar.css";
import {
    PiCakeLight,
    PiCheckCircle,
    PiGiftLight,
    PiTruck,
} from "react-icons/pi";
import { RiShareLine, RiCloseLine, RiZoomInLine, RiArrowLeftSLine, RiArrowRightSLine } from "react-icons/ri";
import "swiper/css/bundle";
import { Navigation, Thumbs } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { z } from "zod";
import { motion, AnimatePresence } from "framer-motion";

const DetalleProducto = ({
    sabor,
    producto,
    cantidadPersonas,
    seccion,
    subseccion,
    productosAleatorios,
    diasBloqueados,
    disponibilidadDespacho,
    imagenes,
    mensajeSelector,
    opcionesSelector,
}: {
    sabor: ISabor;
    producto: IProducto;
    cantidadPersonas: ICantidadPersonas[];
    seccion: ISeccion;
    subseccion: ISubseccion;
    productosAleatorios: ITiendaProductos[];
    diasBloqueados: DiaBloqueado[];
    disponibilidadDespacho: Record<string, DisponibilidadDespacho>;
    imagenes: IImagenesProducto[];
    mensajeSelector: string;
    opcionesSelector: IProductoOpcion[];
}) => {
    const fechasDesactivadas: Date[] = diasBloqueados
        .map((fecha) => {
            if (fecha.dia.toString().includes("/")) {
                const [year, month, day] = fecha.dia.split("/");
                return new Date(`${month}/${day}/${year}`);
            } else if (fecha.dia.toString().includes("-")) {
                const [year, month, day] = fecha.dia.split("-");
                return new Date(`${month}/${day}/${year}`);
            }
            return undefined;
        })
        .filter((value): value is Date => value !== undefined);

    //create a function who checks if today is in fechasDesactivadas, if it is, return the next possible date
    const isSameDay = useCallback((d1: Date, d2: Date) => {
        return (
            d1.getFullYear() === d2.getFullYear() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getDate() === d2.getDate()
        );
    }, []);

    const minDateAllowed = (): Date => {
        let date = new Date();
        for (let i = 0; i < fechasDesactivadas.length; i++) {
            if (isSameDay(fechasDesactivadas[i], date)) {
                date.setDate(date.getDate() + 1);
                i = -1; // Reinicia el ciclo para comprobar desde el principio con la nueva fecha
            }
        }
        return date;
    };

    const desactivarFechas = ({
        activeStartDate,
        date,
        view,
    }: {
        activeStartDate: Date;
        date: Date;
        view: "century" | "decade" | "year" | "month";
    }) => {
        // PRIORIDAD 1: Verificar si está en la lista de días bloqueados
        for (let i = 0; i < fechasDesactivadas.length; i++) {
            if (
                date.getDate() === fechasDesactivadas[i].getDate() &&
                date.getMonth() === fechasDesactivadas[i].getMonth() &&
                date.getFullYear() === fechasDesactivadas[i].getFullYear()
            ) {
                return true; // Bloqueado por bloqueo_dias
            }
        }

        // PRIORIDAD 2: Verificar si está completamente bloqueado por disponibilidad
        const disp = obtenerDisponibilidadFecha(date);
        if (disp && !disp.permite_despacho_domicilio && !disp.permite_retiro_local) {
            return true; // Bloqueado porque no hay ni despacho ni retiro
        }

        return false; // Disponible
    };

    /**
     * Obtener información de disponibilidad de despacho para una fecha
     */
    const obtenerDisponibilidadFecha = useCallback((date: Date): DisponibilidadDespacho | null => {
        const fechaStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        return disponibilidadDespacho[fechaStr] || null;
    }, [disponibilidadDespacho]);

    /**
     * Verificar si un día está bloqueado (tiene prioridad sobre disponibilidad)
     */
    const estaFechaBloqueada = (date: Date): boolean => {
        for (let i = 0; i < fechasDesactivadas.length; i++) {
            if (isSameDay(fechasDesactivadas[i], date)) {
                return true;
            }
        }
        return false;
    };

    /**
     * Agregar clases CSS personalizadas a los días del calendario
     */
    const tileClassName = ({ date, view }: { date: Date; view: string }) => {
        if (view !== 'month') return null;

        if (estaFechaBloqueada(date)) return null;

        const disp = obtenerDisponibilidadFecha(date);
        if (!disp) return null;

        if (!disp.permite_despacho_domicilio && !disp.permite_retiro_local) {
            return 'calendar-bloqueado-total';
        }

        if (!disp.permite_despacho_domicilio && disp.permite_retiro_local) {
            return 'calendar-solo-retiro';
        }

        if (disp.permite_despacho_domicilio && !disp.permite_retiro_local) {
            return 'calendar-solo-despacho';
        }

        return null;
    };

    /**
     * Agregar contenido personalizado a cada día (icono)
     */
    const tileContent = ({ date, view }: { date: Date; view: string }) => {
        if (view !== 'month') return null;

        if (estaFechaBloqueada(date)) return null;

        const disp = obtenerDisponibilidadFecha(date);
        if (!disp || !disp.icono || disp.icono === '✅') return null;

        return (
            <div
                className="calendar-icon-indicator"
                title={disp.mensaje || disp.razon || ''}
            >
                {disp.icono}
            </div>
        );
    };

    const { agregarProducto, vaciarCarrito } = useCartStore();

    const [fechaReserva, setFechaReserva] = useState<CalendarType>(
        minDateAllowed()
    );
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [isZoomOpen, setIsZoomOpen] = useState(false);
    const [zoomIndex, setZoomIndex] = useState<number | null>(null);

    const [cantidadPersonasSeleccionada, setCantidadPersonasSeleccionada] =
        useState<ICantidadPersonas>(cantidadPersonas[0]);

    const [agregaraMensaje, setAgregaraMensaje] = useState<boolean>(false);
    const [mostrarModalMensaje, setMostrarModalMensaje] = useState<boolean>(false);
    const [mensaje, setMensaje] = useState<string>("");

    const [cantidadAComprar, setCantidadAComprar] = useState<number>(1);

    const [edad, setEdad] = useState<number>(-1);
    const [sexo, setSexo] = useState<Sexo>(Sexo.Indicar);

    const [
        decoracionAdicionalSeleccionada,
        setDecoracionAdicionalSeleccionada,
    ] = useState<string | undefined>(opcionesSelector[0]?.opcion ?? undefined);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [tipoDespacho, setTipoDespacho] = useState<string>('Despacho a Domicilio');

    // Obtener disponibilidad de despacho para la fecha seleccionada
    const disponibilidadActual = fechaReserva ? obtenerDisponibilidadFecha(fechaReserva as Date) : null;

    // Efecto para ajustar el tipo de despacho cuando cambia la fecha
    useEffect(() => {
        if (disponibilidadActual) {
            // Si solo permite uno, seleccionarlo automáticamente
            if (disponibilidadActual.permite_despacho_domicilio && !disponibilidadActual.permite_retiro_local) {
                setTipoDespacho('Despacho a Domicilio');
            } else if (!disponibilidadActual.permite_despacho_domicilio && disponibilidadActual.permite_retiro_local) {
                setTipoDespacho('Retiro en Local');
            }
            // Si la opción actual no está permitida, cambiar a la permitida
            else if (tipoDespacho === 'Despacho a Domicilio' && !disponibilidadActual.permite_despacho_domicilio && disponibilidadActual.permite_retiro_local) {
                setTipoDespacho('Retiro en Local');
            } else if (tipoDespacho === 'Retiro en Local' && !disponibilidadActual.permite_retiro_local && disponibilidadActual.permite_despacho_domicilio) {
                setTipoDespacho('Despacho a Domicilio');
            }
        }
    }, [fechaReserva, disponibilidadActual?.permite_despacho_domicilio, disponibilidadActual?.permite_retiro_local]);

    // Estado para el modal de conflicto de carrito
    const [modalConflicto, setModalConflicto] = useState({
        isOpen: false,
        tipo: 'fecha' as 'fecha' | 'tipo_despacho',
        mensaje: '',
        fechaCarrito: '',
        fechaNueva: '',
        datosProducto: {} as any,
    });

    const actualizarCantidadPersonasSeleccionada = useCallback((
        nuevoValor: ICantidadPersonas
    ) => {
        setCantidadPersonasSeleccionada(nuevoValor);
    }, []);

    const actualizarCantidadAComprar = (cantidad: number) => {
        const nuevaCantidad = cantidadAComprar + cantidad;
        const esPosible =
            nuevaCantidad >= 1 &&
            nuevaCantidad <= cantidadPersonasSeleccionada.stock;
        if (esPosible) {
            setCantidadAComprar(cantidadAComprar + cantidad);
        }
    };

    const [activeTab, setActiveTab] = useState<string | undefined>(
        "description"
    );

    const handleSwiper = useCallback((swiper: any) => {
        setThumbsSwiper(swiper);
    }, []);

    const calcularDescuento = (): string => {
        const montoDescuento =
            cantidadPersonasSeleccionada.valor *
            (cantidadPersonasSeleccionada.descuento / 100);

        // Calcular el precio con el descuento aplicado
        const precioConDescuento =
            cantidadPersonasSeleccionada.valor - montoDescuento;

        return precioConDescuento.toFixed(0);
    };

    const handleActiveTab = useCallback((tab: string) => {
        setActiveTab(tab);
    }, []);

    const toggleAgregaraMensaje = useCallback((estado: boolean) => {
        setAgregaraMensaje(estado);
    }, []);

    const formatFechaReserva = (fecha: CalendarType): string => {
        const date = fecha as Date;
        const day = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate();
        const month =
            date.getMonth() + 1 < 10
                ? `0${date.getMonth() + 1}`
                : date.getMonth() + 1;
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    // Handlers para el modal de conflicto
    const handleConfirmarVaciarCarrito = async () => {
        try {
            // CRÍTICO: Primero limpiar el store de Zustand
            vaciarCarrito();

            // Luego vaciar el carrito del backend
            await axios.get(`${window.origin}/api/cart/destroy`);

            // Cerrar el modal
            setModalConflicto({
                ...modalConflicto,
                isOpen: false,
            });

            // Reintentar agregar el producto
            setIsLoading(true);
            await delay(2000);

            const url = `${window.origin}/api/cart/add`;
            const response = await axios.post(url, modalConflicto.datosProducto);

            if (response.status === 200) {
                setIsLoading(false);
                agregarProducto(response.data.producto);
                await PattyToast({
                    message: "Tu producto ha sido agregado al carrito",
                    icon: "success",
                    time: 4000,
                });
            }
        } catch (error) {
            setIsLoading(false);
            capturarError(error);
        }
    };

    const handleCerrarModal = () => {
        setModalConflicto({
            ...modalConflicto,
            isOpen: false,
        });
    };

    const handleCerrarModalMensaje = () => {
        setMostrarModalMensaje(false);
    };

    const handleConfirmarEscribirMensaje = () => {
        setMostrarModalMensaje(false);
        setAgregaraMensaje(true);
        // Pequeño delay para permitir que el DOM se actualice antes de hacer focus
        setTimeout(() => {
            document.getElementById('torta')?.focus();
        }, 100);
    };

    const preAgregarAlCarrito = () => {
        // Mostrar modal solo si permiten mensaje pero el cliente seleccionó que no
        if (sabor.permite_msj === 1 && !agregaraMensaje) {
            setMostrarModalMensaje(true);
            return;
        }
        agregarAlCarrito();
    };

    const agregarAlCarrito = async () => {
        type ProductoData = {
            id_producto: number;
            unidades: number;
            cantidad: number;
            fecha_reserva: string;
            sabor: string;
            mensaje: string;
            edad: number;
            sexo: Sexo;
            decoracionAdicional?: string; // Propiedad opcional
        };

        let data: ProductoData = {
            id_producto: producto.id,
            unidades: cantidadAComprar,
            cantidad: cantidadPersonasSeleccionada.cantidad,
            fecha_reserva: formatFechaReserva(fechaReserva),
            sabor: sabor.sabor,
            mensaje:
                mensaje.trim() === "" ? "Mensaje no indicado" : mensaje.trim(),
            edad: edad,
            sexo: sexo,
        };

        if (sabor.selector_adicional == 1) {
            data.decoracionAdicional = decoracionAdicionalSeleccionada;
        }

        const agregarSchema = z.object({
            id_producto: z.number({ message: "ID de producto inválido" }),
            unidades: z.number().min(1, { message: "Debe seleccionar al menos una unidad" }).max(cantidadPersonasSeleccionada.stock, { message: "No hay stock suficiente" }),
            cantidad: z.number().min(0, { message: "Cantidad de personas invalida inválida" }),
            fecha_reserva: z.string().refine(
                (value) => {
                    //check if value is a valid date, value has dd/mm/yyyy format
                    const date = value.split("/");
                    const day = parseInt(date[0]);
                    const month = parseInt(date[1]);
                    const year = parseInt(date[2]);
                    const dateObject = new Date(year, month - 1, day);
                    return (
                        dateObject.getFullYear() === year &&
                        dateObject.getMonth() === month - 1 &&
                        dateObject.getDate() === day
                    );
                },
                {
                    message: "Formato de fecha incorrecto",
                }
            ),
            sabor: z.string(),
            mensaje: z
                .string()
                .max(40, {
                    message: "El mensaje no puede tener más de 40 caracteres",
                })
                .optional(),
            edad: z.number().int().min(-1).max(120),
            sexo: z.enum([Sexo.Masculino, Sexo.Femenino, Sexo.Indicar]),
            decoracionAdicional: z
                .string()
                .optional()
                .refine(
                    (value) => {
                        if (
                            sabor.selector_adicional === 0 ||
                            (sabor.selector_adicional === 1 &&
                                opcionesSelector.length === 0)
                        ) {
                            return true;
                        }

                        return opcionesSelector.some(
                            (option) => option.opcion === value
                        );
                    },
                    {
                        message: "La decoración seleccionada no es válida",
                    }
                ),
        });

        setIsLoading(true);
        await delay(2000);

        try {
            // Attempting to parse the form data using the form schema
            agregarSchema.parse(data);

            const url = `${window.origin}/api/cart/add`;
            const datosProducto = {
                id_producto: data.id_producto,
                unidades: data.unidades,
                cantidad: data.cantidad,
                fechaReserva: data.fecha_reserva,
                sabor: data.sabor,
                mensaje: data.mensaje,
                edad: data.edad,
                sexo: data.sexo,
                decoracionAdicional: data.decoracionAdicional,
                tipo_despacho: tipoDespacho,
            };

            const response = await axios.post(url, datosProducto);

            if (response.status === 200) {
                setIsLoading(false);
                agregarProducto(response.data.producto);
                await PattyToast({
                    message: "Tu producto ha sido agregado al carrito",
                    icon: "success",
                    time: 4000,
                });
            }
        } catch (error: any) {
            console.log(error);
            setIsLoading(false);

            // Verificar si es un error de conflicto de fecha o tipo de despacho
            if (error.response?.status === 422 && error.response?.data?.type === 'conflicto_fecha') {
                const errorData = error.response.data;
                setModalConflicto({
                    isOpen: true,
                    tipo: 'fecha',
                    mensaje: errorData.message || '',
                    fechaCarrito: errorData.data?.fecha_carrito || '',
                    fechaNueva: errorData.data?.fecha_nueva || '',
                    datosProducto: {
                        id_producto: data.id_producto,
                        unidades: data.unidades,
                        cantidad: data.cantidad,
                        fechaReserva: data.fecha_reserva,
                        sabor: data.sabor,
                        mensaje: data.mensaje,
                        edad: data.edad,
                        sexo: data.sexo,
                        decoracionAdicional: data.decoracionAdicional,
                        tipo_despacho: tipoDespacho,
                    },
                });
            } else if (error.response?.status === 422 && error.response?.data?.type === 'conflicto_tipo_despacho') {
                const errorData = error.response.data;
                setModalConflicto({
                    isOpen: true,
                    tipo: 'tipo_despacho',
                    mensaje: errorData.message || '',
                    fechaCarrito: '',
                    fechaNueva: '',
                    datosProducto: {
                        id_producto: data.id_producto,
                        unidades: data.unidades,
                        cantidad: data.cantidad,
                        fechaReserva: data.fecha_reserva,
                        sabor: data.sabor,
                        mensaje: data.mensaje,
                        edad: data.edad,
                        sexo: data.sexo,
                        decoracionAdicional: data.decoracionAdicional,
                        tipo_despacho: tipoDespacho,
                    },
                });
            } else {
                capturarError(error);
            }
        }
    };

    useEffect(() => {
        if (sabor.selector_adicional === 1) {
            const decoracionNueva = opcionesSelector[0];
            if (decoracionNueva) {
                setDecoracionAdicionalSeleccionada(
                    opcionesSelector[0]?.opcion ?? undefined
                );
            } else {
                setDecoracionAdicionalSeleccionada(undefined);
            }
        }
    }, []);

    useEffect(() => {
        setCantidadAComprar(1);
    }, [cantidadPersonasSeleccionada]);

    useEffect(() => {
        if (!agregaraMensaje) {
            setMensaje("Mensaje no indicado");
            setEdad(-1);
            setSexo(Sexo.Indicar);
        } else {
            setMensaje("");
            setEdad(0);
            setSexo(Sexo.Femenino);
        }
    }, [agregaraMensaje]);

    return (
        <>
            <div className="product-detail sale">
                {isLoading && <CakeLoader />}
                <div className="featured-product md:py-16 py-10">
                    <div className="w-full xl:max-w-[1700px] 2xl:max-w-[1920px] mx-auto px-4 lg:px-8 xl:px-12 flex flex-wrap justify-between gap-y-6">
                        <div className="md:w-[50%] lg:w-[55%] xl:w-[55%] 2xl:w-[50%] lg:pr-10 xl:pr-12 w-full flex flex-col gap-8">
                            {/* Image Gallery */}
                            <div className="list-img w-full flex flex-col-reverse md:flex-row gap-6 items-start">
                                {/* Thumbnails (Left on desktop, Bottom on mobile) */}
                                <div className="w-full md:w-[100px] lg:w-[120px] xl:w-[140px] flex-shrink-0 min-h-[100px] md:h-full md:max-h-[700px]">
                                    <Swiper
                                        onSwiper={handleSwiper}
                                        spaceBetween={12}
                                        slidesPerView={4}
                                        freeMode={true}
                                        watchSlidesProgress={true}
                                        modules={[Navigation, Thumbs]}
                                        direction="horizontal"
                                        breakpoints={{
                                            768: {
                                                direction: "vertical",
                                                slidesPerView: "auto",
                                            }
                                        }}
                                        className="mySwiper w-full h-auto py-1"
                                    >
                                        {imagenes?.map((item) => (
                                            <SwiperSlide key={item.id} className="md:!w-full !h-auto cursor-pointer transition-all duration-300 [&.swiper-slide-thumb-active>img]:border-[#790546] [&.swiper-slide-thumb-active>img]:opacity-100 [&:not(.swiper-slide-thumb-active)>img]:opacity-50 hover:[&:not(.swiper-slide-thumb-active)>img]:opacity-80">
                                                <img
                                                    src={item.ruta}
                                                    width={100}
                                                    height={130}
                                                    loading="lazy"
                                                    alt={`${sabor.nombre_producto} - imagen ${item.id}`}
                                                    className="w-full aspect-[3/4] object-cover rounded-xl border-2 border-transparent transition-all duration-300 object-center shadow-sm"
                                                />
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                </div>

                                {/* Main Image */}
                                <div className="w-full flex-grow relative rounded-2xl overflow-hidden border border-gray-100 shadow-sm group bg-gray-50">
                                    <Swiper
                                        slidesPerView={1}
                                        spaceBetween={0}
                                        thumbs={{ swiper: thumbsSwiper }}
                                        modules={[Thumbs]}
                                        className="mySwiper2 w-full h-full"
                                    >
                                        {imagenes?.map((item, index) => (
                                            <SwiperSlide key={item.id}>
                                                <div
                                                    className="w-full h-full relative cursor-zoom-in group"
                                                    onClick={() => { setZoomIndex(index); setIsZoomOpen(true); }}
                                                >
                                                    <img
                                                        src={item.ruta}
                                                        width={1000}
                                                        height={1300}
                                                        loading={index === 0 ? "eager" : "lazy"}
                                                        alt={`${sabor.nombre_producto} - foto ${index + 1}`}
                                                        className="w-full aspect-[4/5] object-cover bg-gray-50 fotos object-center max-h-[700px] lg:max-h-[800px] xl:max-h-[850px]"
                                                    />
                                                    <div className="absolute bottom-4 right-4 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center text-gray-700 shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                        <RiZoomInLine size={24} />
                                                    </div>
                                                </div>
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                </div>
                            </div>

                            {/* Envios Seguros - Ahora debajo de las imágenes en Desktop */}
                            <div className="get-it w-full hidden md:block border-t border-gray-100 pt-8 mt-2">
                                <div className="heading5 text-center mb-6">Envios Seguros</div>
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="item flex flex-col items-center text-center gap-2">
                                        <div className="icon-delivery-truck text-4xl text-gray-700"></div>
                                        <div>
                                            <div className="text-title text-sm font-semibold">
                                                Refrigeración
                                            </div>
                                            <div className="caption1 text-secondary mt-1 text-xs">
                                                Despacho especializado.
                                            </div>
                                        </div>
                                    </div>
                                    <div className="item flex flex-col items-center text-center gap-2">
                                        <div className="icon-phone-call text-4xl text-gray-700"></div>
                                        <div>
                                            <div className="text-title text-sm font-semibold">
                                                Reservas
                                            </div>
                                            <div className="caption1 text-secondary mt-1 text-xs">
                                                Reserva en cualquier momento.
                                            </div>
                                        </div>
                                    </div>
                                    <div className="item flex flex-col items-center text-center gap-2">
                                        <div className="icon-return text-4xl text-gray-700"></div>
                                        <div>
                                            <div className="text-title text-sm font-semibold">
                                                Despacho protegido
                                            </div>
                                            <div className="caption1 text-secondary mt-1 text-xs">
                                                Enviamos tu pedido seguro.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="product-infor md:w-[48%] lg:w-[42%] xl:w-[40%] 2xl:w-[45%] w-full">
                            <div className="flex justify-between">
                                <div>
                                    <div className="caption2 text-gray-400 font-semibold uppercase">
                                        <p>{subseccion.nombre}</p>
                                    </div>
                                    <div className="heading4 mt-1 text-[#790546]">
                                        {sabor.nombre_producto}
                                    </div>
                                </div>
                                <div
                                    className="w-12 h-12 flex items-center justify-center border border-gray-200 text-gray-600 cursor-pointer rounded-xl transition-all duration-300 hover:bg-[#790546] hover:text-white hover:border-[#790546] shadow-sm ml-4 flex-shrink-0"
                                    onClick={async () => {
                                        if (navigator.share) {
                                            try {
                                                await navigator.share({
                                                    title: sabor.nombre_producto,
                                                    text: '¡Mira esta deliciosa torta en Pastelería Patty Cariño!',
                                                    url: window.location.href,
                                                });
                                            } catch (err) {
                                                console.error('Error sharing:', err);
                                            }
                                        } else {
                                            navigator.clipboard.writeText(window.location.href);
                                            PattyToast({
                                                icon: "success",
                                                message: "¡Enlace copiado al portapapeles!",
                                            });
                                        }
                                    }}
                                    title="Compartir Producto"
                                >
                                    <RiShareLine size={24} className="currentColor" />
                                </div>
                            </div>
                            <div className="product-price-block flex items-center gap-2 flex-wrap mt-1 duration-300 relative z-[1]">
                                <div className="text-3xl font-semibold">
                                    ${separadorMiles(calcularDescuento())}
                                </div>
                                {cantidadPersonasSeleccionada.descuento > 0 && (
                                    <>
                                        <div
                                            className="product-origin-price caption1 text-gray-400 line-through
"
                                        >
                                            $
                                            {separadorMiles(
                                                cantidadPersonasSeleccionada.valor
                                            )}
                                        </div>
                                        <div className="product-sale caption1 font-medium bg-green px-3 py-0.5 inline-block rounded-full">
                                            {
                                                cantidadPersonasSeleccionada.descuento
                                            }
                                            %
                                        </div>
                                    </>
                                )}
                            </div>
                            <div
                                className="flex items-center mt-3"
                                dangerouslySetInnerHTML={{
                                    __html: sabor.descripcion,
                                }}
                            ></div>

                            <div className="list-action mt-6">
                                {/* cantidad personas */}
                                <div className="choose-color mt-5">
                                    <div className="text-title">
                                        Cantidad:
                                        <div className="flex flex-wrap gap-2 mt-3">
                                            {cantidadPersonas.map((item) => (
                                                <div
                                                    className={`w-fit relative flex select-none items-center justify-center whitespace-nowrap rounded-xl border py-2 px-4 text-sm font-medium transition-all cursor-pointer ${cantidadPersonasSeleccionada.id == item.id
                                                        ? "bg-[#790546] text-white border-[#790546] shadow-md shadow-[#790546]/20"
                                                        : "bg-white text-gray-700 border-gray-200 hover:border-[#790546]/50 hover:bg-gray-50 active:scale-[0.98]"
                                                        }`}
                                                    key={item.id}
                                                    onClick={() =>
                                                        actualizarCantidadPersonasSeleccionada(item)
                                                    }
                                                >
                                                    <span>
                                                        {item.cantidad} {item.medida}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* selector adicional */}
                                {sabor.selector_adicional == 1 &&
                                    opcionesSelector.length > 0 ? (
                                    <div className=" mt-5">
                                        <div className="heading flex items-center justify-between">
                                            <div className="text-title w-full">
                                                {mensajeSelector}
                                                <select
                                                    className="border-line border-[1px] px-4 pt-3 pb-3 w-full rounded-lg bg-gray-50"
                                                    onChange={(e) => {
                                                        setDecoracionAdicionalSeleccionada(
                                                            e.target.value
                                                        );
                                                    }}
                                                >
                                                    {opcionesSelector.map(
                                                        (item, index) => (
                                                            <option
                                                                key={item.id}
                                                                value={
                                                                    item.opcion
                                                                }
                                                                className="border-line border-[1px] px-4 pt-3 pb-3 w-full rounded-lg bg-gray-200 text-[#790546]"
                                                            >
                                                                {item.opcion}
                                                            </option>
                                                        )
                                                    )}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                ) : null}

                                {/* switch mensaje */}

                                {sabor.permite_msj === 1 && (
                                    <>
                                        <div className="mt-5">
                                            {/* Mensaje informativo en gris */}
                                            <div className="mb-3">
                                                <p className="text-sm text-gray-500 font-medium">
                                                    Si deseas escribir un mensaje en tu torta debes activar esta casilla.
                                                </p>
                                            </div>

                                            {/* Contenedor principal con sombra atractiva */}
                                            <div className="w-full flex flex-row justify-between bg-[#790546] p-2 rounded-xl text-white items-center shadow-[0_0_20px_rgba(121,5,70,0.6)]">
                                                <div className="flex">
                                                    <p className="text-md">
                                                        ¿Deseas escribir un mensaje en tu torta?
                                                    </p>
                                                </div>
                                                <div className="flex gap-6 items-center">
                                                    <div className="flex items-center">
                                                        <label className="relative flex items-center p-2 rounded-full cursor-pointer">
                                                            <input
                                                                name="type"
                                                                type="radio"
                                                                className="before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-full border border-gray-200 text-white transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-gray-500 before:opacity-0 before:transition-opacity checked:border-gray-200 checked:before:bg-gray-400 hover:before:opacity-10"
                                                                checked={
                                                                    agregaraMensaje ===
                                                                    true
                                                                }
                                                                onChange={() =>
                                                                    null
                                                                }
                                                                onClick={() =>
                                                                    toggleAgregaraMensaje(
                                                                        true
                                                                    )
                                                                }
                                                            />
                                                            <span className="absolute text-white transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100">
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    className="h-3.5 w-3.5"
                                                                    viewBox="0 0 16 16"
                                                                    fill="currentColor"
                                                                >
                                                                    <circle
                                                                        data-name="ellipse"
                                                                        cx={8}
                                                                        cy={8}
                                                                        r={8}
                                                                    />
                                                                </svg>
                                                            </span>
                                                        </label>
                                                        <label className="ml-1 mt-px font-light text-white cursor-pointer select-none">
                                                            Si
                                                        </label>
                                                    </div>
                                                    <div className="flex items-center">
                                                        <label className="relative flex items-center p-2 rounded-full cursor-pointer">
                                                            <input
                                                                name="type"
                                                                type="radio"
                                                                className="before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-full border border-gray-200 text-white transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-gray-500 before:opacity-0 before:transition-opacity checked:border-gray-200 checked:before:bg-gray-400 hover:before:opacity-10"
                                                                checked={
                                                                    agregaraMensaje ===
                                                                    false
                                                                }
                                                                onChange={() =>
                                                                    null
                                                                }
                                                                onClick={() =>
                                                                    toggleAgregaraMensaje(
                                                                        false
                                                                    )
                                                                }
                                                            />
                                                            <span className="absolute text-white transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100">
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    className="h-3.5 w-3.5"
                                                                    viewBox="0 0 16 16"
                                                                    fill="currentColor"
                                                                >
                                                                    <circle
                                                                        data-name="ellipse"
                                                                        cx={8}
                                                                        cy={8}
                                                                        r={8}
                                                                    />
                                                                </svg>
                                                            </span>
                                                        </label>
                                                        <label className="ml-1 mt-px  text-white cursor-pointer select-none">
                                                            No
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {agregaraMensaje === true && (
                                            <>
                                                <div className="mt-5">
                                                    <div className="heading flex items-center justify-between">
                                                        <div className="w-full">
                                                            Mensaje en tu torta:
                                                            <textarea
                                                                className="border-line px-4 pt-3 pb-3 w-full rounded-lg bg-gray-50 border-none"
                                                                id="torta"
                                                                placeholder="Que escribimos en tu torta ?"
                                                                value={mensaje}
                                                                onChange={(e) =>
                                                                    setMensaje(
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                                maxLength={250}
                                                                style={{
                                                                    maxHeight:
                                                                        "150px",
                                                                    resize: "none",
                                                                    overflowY:
                                                                        "auto",
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex">
                                                    <div className="flex flex-col w-1/2 p-2">
                                                        Edad
                                                        <input
                                                            className="border-line px-4 pt-3 pb-3 w-full rounded-lg bg-gray-50 border-none"
                                                            id="edad"
                                                            type="number"
                                                            placeholder="Ej: 32 años"
                                                            min={0}
                                                            max={120}
                                                            defaultValue={0}
                                                            onChange={(e) => {
                                                                if (
                                                                    Number(
                                                                        e.target
                                                                            .value
                                                                    ) < 0 ||
                                                                    Number(
                                                                        e.target
                                                                            .value
                                                                    ) > 120 ||
                                                                    isNaN(
                                                                        Number(
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    )
                                                                ) {
                                                                    e.target.value = `0`;
                                                                    setEdad(0);
                                                                } else {
                                                                    e.target.value = `${parseInt(
                                                                        e.target
                                                                            .value
                                                                    )}`;
                                                                    setEdad(
                                                                        parseInt(
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="flex flex-col w-1/2 p-2">
                                                        Sexo
                                                        <select
                                                            className="border-line px-4 pt-3 pb-3 w-full rounded-lg bg-gray-50 border-none"
                                                            id="sexo"
                                                            defaultValue={
                                                                Sexo.Femenino
                                                            }
                                                            onChange={(e) => {
                                                                setSexo(
                                                                    e.target
                                                                        .value as Sexo
                                                                );
                                                            }}
                                                        >
                                                            <option
                                                                value={
                                                                    Sexo.Masculino
                                                                }
                                                            >
                                                                Masculino
                                                            </option>
                                                            <option
                                                                value={
                                                                    Sexo.Femenino
                                                                }
                                                            >
                                                                Femenino
                                                            </option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}

                                {/* Tipo de Despacho - Selector Mejorado */}
                                <div className="mt-5">
                                    <div className="text-title mb-3">Tipo de Despacho:</div>
                                    {disponibilidadActual && !disponibilidadActual.permite_despacho_domicilio && !disponibilidadActual.permite_retiro_local ? (
                                        <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl text-center">
                                            <span className="text-sm text-red-600 font-semibold">
                                                ⚠️ No hay opciones de despacho disponibles para esta fecha
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-2 gap-3">
                                            <label
                                                className={`relative flex items-center justify-center p-4 rounded-xl border-2 transition-all duration-200 ${disponibilidadActual && !disponibilidadActual.permite_despacho_domicilio
                                                    ? 'opacity-50 cursor-not-allowed border-gray-200 bg-gray-50'
                                                    : tipoDespacho === 'Despacho a Domicilio'
                                                        ? 'border-[#790546] bg-[#790546]/5 shadow-md cursor-pointer'
                                                        : 'border-gray-200 hover:border-gray-300 bg-white cursor-pointer'
                                                    }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="tipoDespacho"
                                                    value="Despacho a Domicilio"
                                                    checked={tipoDespacho === 'Despacho a Domicilio'}
                                                    onChange={(e) => setTipoDespacho(e.target.value)}
                                                    disabled={disponibilidadActual ? !disponibilidadActual.permite_despacho_domicilio : false}
                                                    className="sr-only"
                                                />
                                                <div className="flex flex-col items-center gap-2 text-center">
                                                    <div className={`text-2xl ${tipoDespacho === 'Despacho a Domicilio' ? 'scale-110' : ''} transition-transform`}>
                                                        🚚
                                                    </div>
                                                    <span className={`text-sm font-semibold ${disponibilidadActual && !disponibilidadActual.permite_despacho_domicilio
                                                        ? 'text-gray-400'
                                                        : tipoDespacho === 'Despacho a Domicilio' ? 'text-[#790546]' : 'text-gray-700'
                                                        }`}>
                                                        Despacho a Domicilio
                                                    </span>
                                                    {disponibilidadActual && !disponibilidadActual.permite_despacho_domicilio && (
                                                        <span className="text-xs text-gray-400">No disponible</span>
                                                    )}
                                                </div>
                                                {tipoDespacho === 'Despacho a Domicilio' && (!disponibilidadActual || disponibilidadActual.permite_despacho_domicilio) && (
                                                    <div className="absolute top-2 right-2 w-6 h-6 bg-[#790546] rounded-full flex items-center justify-center">
                                                        <svg className="w-4 h-4 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path d="M5 13l4 4L19 7"></path>
                                                        </svg>
                                                    </div>
                                                )}
                                            </label>

                                            <label
                                                className={`relative flex items-center justify-center p-4 rounded-xl border-2 transition-all duration-200 ${disponibilidadActual && !disponibilidadActual.permite_retiro_local
                                                    ? 'opacity-50 cursor-not-allowed border-gray-200 bg-gray-50'
                                                    : tipoDespacho === 'Retiro en Local'
                                                        ? 'border-[#790546] bg-[#790546]/5 shadow-md cursor-pointer'
                                                        : 'border-gray-200 hover:border-gray-300 bg-white cursor-pointer'
                                                    }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="tipoDespacho"
                                                    value="Retiro en Local"
                                                    checked={tipoDespacho === 'Retiro en Local'}
                                                    onChange={(e) => setTipoDespacho(e.target.value)}
                                                    disabled={disponibilidadActual ? !disponibilidadActual.permite_retiro_local : false}
                                                    className="sr-only"
                                                />
                                                <div className="flex flex-col items-center gap-2 text-center">
                                                    <div className={`text-2xl ${tipoDespacho === 'Retiro en Local' ? 'scale-110' : ''} transition-transform`}>
                                                        🏪
                                                    </div>
                                                    <span className={`text-sm font-semibold ${disponibilidadActual && !disponibilidadActual.permite_retiro_local
                                                        ? 'text-gray-400'
                                                        : tipoDespacho === 'Retiro en Local' ? 'text-[#790546]' : 'text-gray-700'
                                                        }`}>
                                                        Retiro en Local
                                                    </span>
                                                    {disponibilidadActual && !disponibilidadActual.permite_retiro_local && (
                                                        <span className="text-xs text-gray-400">No disponible</span>
                                                    )}
                                                </div>
                                                {tipoDespacho === 'Retiro en Local' && (!disponibilidadActual || disponibilidadActual.permite_retiro_local) && (
                                                    <div className="absolute top-2 right-2 w-6 h-6 bg-[#790546] rounded-full flex items-center justify-center">
                                                        <svg className="w-4 h-4 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path d="M5 13l4 4L19 7"></path>
                                                        </svg>
                                                    </div>
                                                )}
                                            </label>
                                        </div>
                                    )}

                                    {tipoDespacho === 'Despacho a Domicilio' && (!disponibilidadActual || disponibilidadActual.permite_despacho_domicilio) && (
                                        <div className="mt-3 p-3 sm:px-4 bg-[#790546]/5 border border-[#790546]/20 rounded-xl flex gap-3 text-sm text-gray-700 items-start">
                                            <span className="text-lg leading-none mt-0.5">🚚</span>
                                            <div>
                                                <p><strong>Horario de entrega:</strong> 10:00 a 20:00 hrs.</p>
                                                <p className="text-xs text-gray-500 mt-1">* Las entregas no tienen un horario fijo.</p>
                                            </div>
                                        </div>
                                    )}
                                    {tipoDespacho === 'Retiro en Local' && (!disponibilidadActual || disponibilidadActual.permite_retiro_local) && (
                                        <div className="mt-3 p-3 sm:px-4 bg-[#790546]/5 border border-[#790546]/20 rounded-xl flex gap-3 text-sm text-gray-700 items-start">
                                            <span className="text-lg leading-none mt-0.5">🏪</span>
                                            <div>
                                                <p><strong>Retiro en tienda:</strong> Sujeto al horario de atención de la sucursal Pedidos.</p>
                                                <a href="/ubicacion" target="_blank" className="inline-flex items-center gap-1 text-[#790546] font-semibold hover:underline mt-1 text-xs">
                                                    Ver horarios de atención
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
                                                </a>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Calendario */}
                                <div className=" mt-5">
                                    <div className="heading flex items-center justify-between">
                                        <div className="text-title w-full">
                                            Fecha Reserva:
                                            <Suspense fallback={<div className="w-full h-[300px] bg-gray-100 rounded-lg animate-pulse" />}>
                                                <Calendar
                                                    onChange={setFechaReserva}
                                                    value={fechaReserva}
                                                    minDate={minDateAllowed()}
                                                    tileDisabled={desactivarFechas}
                                                    tileClassName={tileClassName}
                                                    tileContent={tileContent}
                                                    locale="es"
                                                />
                                            </Suspense>
                                        </div>
                                    </div>
                                </div>

                                <div className="text-title mt-5">Cantidad:</div>

                                <div className="choose-quantity flex items-center lg:justify-between gap-5 gap-y-3 mt-3">
                                    <div className="quantity-block md:p-3 max-md:py-1.5 max-md:px-3 flex items-center justify-between rounded-lg border border-line sm:w-[180px] w-[120px] flex-shrink-0">
                                        <Icon.Minus
                                            size={20}
                                            onClick={() =>
                                                actualizarCantidadAComprar(-1)
                                            }
                                            className={`${cantidadPersonasSeleccionada.stock <=
                                                0
                                                ? "disabled"
                                                : ""
                                                } cursor-pointer`}
                                        />
                                        <div className="body1 font-semibold">
                                            {cantidadAComprar}
                                        </div>
                                        <Icon.Plus
                                            size={20}
                                            onClick={() =>
                                                actualizarCantidadAComprar(1)
                                            }
                                            className="cursor-pointer"
                                        />
                                    </div>
                                    <div
                                        onClick={() => preAgregarAlCarrito()}
                                        className="button-main w-full text-center bg-white text-black border border-black cursor-pointer"
                                    >
                                        Agregar
                                    </div>
                                </div>

                                <div className="list-payment mt-7">
                                    <div className="main-content lg:pt-8 pt-6 lg:pb-6 pb-4 sm:px-4 px-3 border border-line rounded-xl relative max-md:w-2/3 max-sm:w-full">
                                        <div className="heading6 px-5 bg-white absolute -top-[14px] left-1/2 -translate-x-1/2 whitespace-nowrap">
                                            Pago Seguro
                                        </div>
                                        <div className="list grid grid-cols-6">
                                            <div className="item flex items-center justify-center lg:px-3 px-1">
                                                <img
                                                    src={
                                                        "https://www.flow.cl/img/logo_webpay.png"
                                                    }
                                                    width={500}
                                                    height={450}
                                                    alt="payment"
                                                    className="w-full"
                                                />
                                            </div>
                                            <div className="item flex items-center justify-center lg:px-3 px-1">
                                                <img
                                                    src={
                                                        "https://www.flow.cl/img/logo_transfer.png?v=1"
                                                    }
                                                    width={500}
                                                    height={450}
                                                    alt="payment"
                                                    className="w-full"
                                                />
                                            </div>
                                            <div className="item flex items-center justify-center lg:px-3 px-1">
                                                <img
                                                    src={
                                                        "https://www.flow.cl/img/logo_onepay.png"
                                                    }
                                                    width={500}
                                                    height={450}
                                                    alt="payment"
                                                    className="w-full"
                                                />
                                            </div>
                                            <div className="item flex items-center justify-center lg:px-3 px-1">
                                                <img
                                                    src={
                                                        "https://www.flow.cl/img/logo_mach.png"
                                                    }
                                                    width={500}
                                                    height={450}
                                                    alt="payment"
                                                    className="w-full"
                                                />
                                            </div>
                                            <div className="item flex items-center justify-center lg:px-3 px-1">
                                                <img
                                                    src={
                                                        "https://www.flow.cl/img/logo_check.png"
                                                    }
                                                    width={500}
                                                    height={450}
                                                    alt="payment"
                                                    className="w-full"
                                                />
                                            </div>
                                            <div className="item flex items-center justify-center lg:px-3 px-1">
                                                <img
                                                    src={
                                                        "https://www.flow.cl/images/header/logo-flow.svg"
                                                    }
                                                    width={500}
                                                    height={450}
                                                    alt="payment"
                                                    className="w-full"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="get-it mt-6 md:hidden">
                                <div className="heading5">Envios Seguros</div>
                                <div className="item flex items-center gap-3 mt-4">
                                    <div className="icon-delivery-truck text-4xl"></div>
                                    <div>
                                        <div className="text-title">
                                            Refrigeracion
                                        </div>
                                        <div className="caption1 text-secondary mt-1">
                                            Despacho especializado.
                                        </div>
                                    </div>
                                </div>
                                <div className="item flex items-center gap-3 mt-4">
                                    <div className="icon-phone-call text-4xl"></div>
                                    <div>
                                        <div className="text-title">
                                            Reservas
                                        </div>
                                        <div className="caption1 text-secondary mt-1">
                                            Puedes reservar tu pedido en
                                            cualquier momento.
                                        </div>
                                    </div>
                                </div>
                                <div className="item flex items-center gap-3 mt-4">
                                    <div className="icon-return text-4xl"></div>
                                    <div>
                                        <div className="text-title">
                                            Despacho protegido
                                        </div>
                                        <div className="caption1 text-secondary mt-1">
                                            Enviamos tu pedido de la manera mas
                                            segura para que obtengas el mejor
                                            producto.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="desc-tab md:pb-20 pb-10">
                    <div className="container">
                        <div className="flex items-center justify-center w-full">
                            <div className="menu-tab flex items-center md:gap-[60px] gap-8">
                                <div
                                    className={`tab-item heading5 has-line-before text-secondary2 hover:text-black duration-300 ${activeTab === "description"
                                        ? "active"
                                        : ""
                                        }`}
                                    onClick={() =>
                                        handleActiveTab("description")
                                    }
                                >
                                    Descripcion{" "}
                                </div>
                                <div
                                    className={`tab-item heading5 has-line-before text-secondary2 hover:text-black duration-300 ${activeTab === "specifications"
                                        ? "active"
                                        : ""
                                        }`}
                                    onClick={() =>
                                        handleActiveTab("specifications")
                                    }
                                >
                                    Especificaciones
                                </div>
                            </div>
                        </div>
                        <div className="desc-block mt-8">
                            <div
                                className={`desc-item description ${activeTab === "description" ? "open" : ""
                                    }`}
                            >
                                <div className="grid md:grid-cols-2 gap-8 gap-y-5">
                                    <div className="left">
                                        <div
                                            className="text-secondary mt-2"
                                            dangerouslySetInnerHTML={{
                                                __html: sabor.descripcion,
                                            }}
                                        ></div>
                                    </div>
                                    <div className="right">
                                        <div className="heading6">
                                            Sobre nuestros productos
                                        </div>
                                        <div className="list-feature">
                                            <div className="item flex gap-1 text-secondary mt-1">
                                                <Icon.Dot size={28} />
                                                <p>
                                                    Ingredientes frescos y de
                                                    alta calidad.
                                                </p>
                                            </div>
                                            <div className="item flex gap-1 text-secondary mt-1">
                                                <Icon.Dot size={28} />
                                                <p>Elaboración artesanal.</p>
                                            </div>
                                            <div className="item flex gap-1 text-secondary mt-1">
                                                <Icon.Dot size={28} />
                                                <p>Variedad y creatividad.</p>
                                            </div>
                                            <div className="item flex gap-1 text-secondary mt-1">
                                                <Icon.Dot size={28} />
                                                <p>
                                                    Frescura y aspecto
                                                    apetecible.
                                                </p>
                                            </div>
                                            <div className="item flex gap-1 text-secondary mt-1">
                                                <Icon.Dot size={28} />
                                                <p>
                                                    Atención al cliente
                                                    personalizada.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="grid lg:grid-cols-4 grid-cols-2 gap-[30px] md:mt-10 mt-6">
                                    <div className="item">
                                        <PiTruck size={38} />

                                        <div className="heading font-bold mt-4">
                                            Nosotros si despachamos
                                        </div>
                                        <div className="text-secondary mt-2">
                                            Enviamos tus pedidos de manera
                                            segura y protegidos en la RM.
                                        </div>
                                    </div>
                                    <div className="item">
                                        <PiCakeLight size={38} />
                                        <div className="heading font-bold mt-4">
                                            Innovación en sabores
                                        </div>
                                        <div className="text-secondary mt-2">
                                            Experimentar con sabores únicos y
                                            presentaciones creativas.
                                        </div>
                                    </div>
                                    <div className="item">
                                        <PiGiftLight size={38} />
                                        <div className="heading mt-4 font-bold">
                                            Acumula Puntos{" "}
                                        </div>
                                        <div className="text-secondary mt-2">
                                            Al comprar por nuestra web acumulas
                                            puntos para futuros dctos.
                                        </div>
                                    </div>
                                    <div className="item">
                                        <PiCheckCircle size={38} />
                                        <div className="heading  font-bold mt-4">
                                            Calidad y frescura de ingredientes{" "}
                                        </div>
                                        <div className="text-secondary mt-2">
                                            Utilizar ingredientes frescos y de
                                            alta calidad en todos los productos.
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                className={`desc-item specifications flex items-center justify-center ${activeTab === "specifications" ? "open" : ""
                                    }`}
                            >
                                <div className="lg:w-1/2 sm:w-3/4 w-full">
                                    <div className="item bg-surface flex items-center gap-8 py-3 px-10">
                                        <div className="text-title sm:w-1/4 w-1/3">
                                            Tipo
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <p>{seccion.nombre}</p>
                                        </div>
                                    </div>
                                    <div className="item flex items-center gap-8 py-3 px-10">
                                        <div className="text-title sm:w-1/4 w-1/3">
                                            Producto
                                        </div>
                                        <p>{subseccion.nombre}</p>
                                    </div>
                                    <div className="item bg-surface flex items-center gap-8 py-3 px-10">
                                        <div className="text-title sm:w-1/4 w-1/3">
                                            Sabor
                                        </div>
                                        <p>{sabor.sabor}</p>
                                    </div>
                                    <div className="item flex items-center gap-8 py-3 px-10">
                                        <div className="text-title sm:w-1/4 w-1/3">
                                            Cantidad Personas
                                        </div>
                                        <p>
                                            {formater.format(
                                                cantidadPersonas.map((item) =>
                                                    item.cantidad.toString()
                                                )
                                            )}{" "}
                                            personas
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* productos relacionados */}
                <div className="related-product md:py-20 py-10">
                    <div className="container">
                        <div className="flex flex-col items-center text-center mb-10 w-full">
                            <div className="flex items-center justify-center gap-3 w-full mb-3">
                                <PiCakeLight className="w-8 h-8 text-[#790546] shrink-0" />
                                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight shrink-0">
                                    Productos Relacionados
                                </h2>
                                <div className="w-8 h-8 shrink-0 opacity-0 pointer-events-none"></div>
                            </div>
                            <p className="text-gray-500 text-sm md:text-base mt-2 max-w-lg text-balance">
                                También te podría interesar
                            </p>
                            <div className="w-16 h-1 bg-[#790546] rounded-full mt-4"></div>
                        </div>
                        <div className="list-product hide-product-sold  grid lg:grid-cols-4 grid-cols-2 md:gap-[30px] gap-5 md:mt-10 mt-6">
                            {productosAleatorios.map((item, index) => (
                                <Product key={index} data={item} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Image Zoom Modal */}
            <AnimatePresence>
                {isZoomOpen && zoomIndex !== null && imagenes && imagenes[zoomIndex] && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
                        onClick={() => setIsZoomOpen(false)}
                    >
                        <div className="absolute top-6 right-6 text-white text-4xl cursor-pointer hover:text-gray-300 z-[10000]">
                            <RiCloseLine />
                        </div>

                        {imagenes.length > 1 && (
                            <button
                                className="absolute left-2 md:left-8 text-white text-4xl md:text-5xl hover:text-gray-300 z-[10000] p-2 transition-transform hover:scale-110"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setZoomIndex((zoomIndex - 1 + imagenes.length) % imagenes.length);
                                }}
                            >
                                <RiArrowLeftSLine />
                            </button>
                        )}

                        <img
                            src={imagenes[zoomIndex].ruta}
                            alt="Zoom Producto"
                            className="max-w-[95vw] max-h-[90vh] object-contain rounded-xl shadow-2xl bg-white p-2 md:p-4 cursor-default"
                            onClick={(e) => e.stopPropagation()}
                        />

                        {imagenes.length > 1 && (
                            <button
                                className="absolute right-2 md:right-8 text-white text-4xl md:text-5xl hover:text-gray-300 z-[10000] p-2 transition-transform hover:scale-110"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setZoomIndex((zoomIndex + 1) % imagenes.length);
                                }}
                            >
                                <RiArrowRightSLine />
                            </button>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Modal de conflicto de carrito */}
            <ModalConflictoCarrito
                isOpen={modalConflicto.isOpen}
                onClose={handleCerrarModal}
                onConfirm={handleConfirmarVaciarCarrito}
                tipo={modalConflicto.tipo}
                mensaje={modalConflicto.mensaje}
                fechaCarrito={modalConflicto.fechaCarrito}
                fechaNueva={modalConflicto.fechaNueva}
            />

            {/* Modal de confirmación si olvidan el mensaje */}
            {mostrarModalMensaje && (
                <>
                    <div className="fixed inset-0 bg-black/50 z-[9999]" onClick={handleCerrarModalMensaje}></div>
                    <div className="fixed inset-0 flex items-center justify-center z-[10000] p-4 pointer-events-none">
                        <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 max-w-md w-full pointer-events-auto flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-[#790546]/10 rounded-full flex items-center justify-center mb-4">
                                <span className="text-3xl">📝</span>
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">
                                ¿Deseas incluir un mensaje?
                            </h3>
                            <p className="text-gray-600 mb-6 text-sm sm:text-base">
                                Has indicado que no deseas un mensaje en tu pedido. ¿Estás seguro de continuar sin una dedicatoria?
                            </p>
                            <div className="w-full flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={() => {
                                        handleCerrarModalMensaje();
                                        agregarAlCarrito();
                                    }}
                                    className="px-4 py-2.5 rounded-xl border-2 border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 hover:border-gray-300 transition-all cursor-pointer active:scale-[0.98] flex-1"
                                >
                                    Continuar sin mensaje
                                </button>
                                <button
                                    onClick={handleConfirmarEscribirMensaje}
                                    className="px-4 py-2.5 rounded-xl bg-[#790546] text-white font-semibold hover:bg-[#6a0440] transition-all cursor-pointer shadow-md active:scale-[0.98] flex-1"
                                >
                                    Escribir un mensaje
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};
DetalleProducto.layout = (page: ReactNode) => (
    <LayoutPrincipal children={page} />
);
export default DetalleProducto;
